import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  IconButton, 
  Button, 
  TableContainer, 
  Table, 
  TableBody, 
  TableRow, 
  TableCell, 
  FormControl,
  FormHelperText,
  OutlinedInput,
  Collapse,
  Alert,
  Autocomplete,
  MenuItem,
  TextField,
  Box,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import typography from '../../cssUtils/typography';
import ButtonOne from '../../common/normal-button';
import AppDatePicker from '../../common/app-date-picker.jsx';
import CountryCodeSelect from '../../common/country-code-select.jsx';
import { getPhoneValidationError } from '../../common/phone-validation';
import { getCountryCallingCode, parsePhoneNumber } from 'react-phone-number-input';

// Client.phone_no is stored as one digit string with the country code
// ("919820075989"). Split it for the form; legacy 10-digit numbers stored
// without a code are taken as Indian.
const splitClientPhone = (stored) => {
  const digits = String(stored || '').replace(/\D/g, '');
  if (digits.length !== 10) {
    try {
      const parsed = parsePhoneNumber('+' + digits);
      if (parsed?.country) return { country_iso: parsed.country, phone_no: parsed.nationalNumber };
    } catch (e) {}
  }
  return { country_iso: 'IN', phone_no: digits };
};

// Form state holds the national number + ISO country; the API gets the
// combined number plus the calling code.
const toPhonePayload = ({ country_iso, phone_no, ...rest }) => {
  const country_code = getCountryCallingCode(country_iso || 'IN');
  return { ...rest, phone_no: country_code + phone_no, country_code };
};

// Same per-country check as the Audience modals, so a number the backend
// can't sync to Audience (wrong length/pattern for the country) is caught here.
const getClientPhoneError = ({ country_iso, phone_no }) =>
  getPhoneValidationError(phone_no, country_iso || 'IN', getCountryCallingCode(country_iso || 'IN'));

const EMPTY_CLIENT_FORM = {
  country_iso: 'IN',
  phone_no: '',
  client_code: '',
  family_code: '',
  client_name: '',
  date_of_birth: null,
};

const CustomersFormDialog = ({ modalState, handleModalState, selectedClient }) => {
  const tenant = localStorage.getItem('tenant');
  const URL = process.env.NODE_ENV === 'production' ? 'api' : `http://localhost:8000/api`;
  const [formData, setFormData] = useState({
    country_iso: 'IN',
    phone_no: '',
    client_code: '',
    family_code: '',
    client_name: '',
    reporting_manager: '',
    date_of_birth: null,
  });

  const [formErrors, setFormErrors] = useState({
    phone_no: '',
    client_code: '',
    family_code: '',
    client_name: '',
    date_of_birth: '',
  });

  const [alertObj, setAlertObj] = useState({
    "active": false,
    "msg": "",
    "type": ""
  });

  const handleAlertClose = () => {
    setAlertObj({
      "active": false,
      "type": "",
      "msg": ""
    });
  };

  const formGroupStyle = {
    marginBottom: '15px',
  };

  const [reportingManagers, setReportingManagers] = useState([]);

  useEffect(() => {
    axios.get(`${URL}/rm-dashboard/rm/list/all/`, {headers: {
                "X-Tenant-Id": tenant
    }})
      .then(response => {
        setReportingManagers(response.data.results);
      })
      .catch(error => {
        console.error('Error fetching relationship managers:', error);
      });
  }, []);

  const handlePopupClose = () => {
        handleModalState({"isOpen":false, "popupType": modalState.popupType});
        handleAlertClose();
        // Next Add starts from an empty form.
        if (!selectedClient) setFormData(EMPTY_CLIENT_FORM);

        setFormErrors({
          phone_no: '',
          client_code: '',
          family_code: '',
          client_name: '',
          date_of_birth: '',
        });
      };

  useEffect(() => {
    // If selectedClient is provided, update formData with its values
    if (selectedClient) {
      setFormData({
        ...selectedClient,
        ...splitClientPhone(selectedClient.phone_no),
        date_of_birth: selectedClient.date_of_birth ? dayjs(selectedClient.date_of_birth) : null,
      });
    } else {
      // If no selectedClient is provided (i.e., adding new client), reset formData
      setFormData(EMPTY_CLIENT_FORM);
    }
  }, [selectedClient]);

  const clearFieldError = (name) => setFormErrors((prev) => ({ ...prev, [name]: '' }));

  // Live phone check, same as the Add Audience modal: derived from the current
  // number + country, so it clears itself as soon as the number is valid.
  const phoneError = formData.phone_no ? getClientPhoneError(formData) : null;

  const updatePhone = (country_iso, phone_no) => {
    setFormData((prev) => ({ ...prev, country_iso, phone_no }));
    clearFieldError('phone_no');
  };

  const handleDateChange = (newValue) => {
    setFormData((prev) => ({ ...prev, date_of_birth: newValue }));
    clearFieldError('date_of_birth');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone_no') {
      updatePhone(formData.country_iso, value.replace(/\D/g, ''));
      return;
    }
    setFormData({ ...formData, [name]: value });
    clearFieldError(name);
  };

  const handleAdd = () => {
    const newFormErrors = {};
    let hasErrors = false;
    let eamilPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

    const phoneError = getClientPhoneError(formData);
    if (phoneError) {
      newFormErrors.phone_no = phoneError;
      hasErrors = true;
    }

    if (formData.client_code.trim() === '') {
      newFormErrors.client_code = 'please enter a client code';
      hasErrors = true;
    }
    if (formData.client_name.trim() === '') {
      newFormErrors.client_name = 'please enter a client name';
      hasErrors = true;
    }
    console.log("formData.reporting_manager: ", formData.reporting_manager);
    if (formData.reporting_manager === null || formData.reporting_manager === '' || formData.reporting_manager === undefined) {
      newFormErrors.reporting_manager = 'please select a relationship manager';
      hasErrors = true;
    }


    if (hasErrors) {
      setFormErrors(newFormErrors);
      return;
    }

    // API endpoint
    const apiUrl = URL + '/clients/clients/';

    const payload = {
      ...toPhonePayload(formData),
      date_of_birth: formData.date_of_birth ? dayjs(formData.date_of_birth).format('YYYY-MM-DD') : null,
    };

    // API call
    axios.post(apiUrl, payload, {
      headers: {
                "X-Tenant-Id": tenant
            }})
      .then(response => {
        console.log('API Response:', response.data);
        // Handle success, e.g., show a success message
        // Optionally, you can also update your UI or fetch new data
        // audience_status "updated" = the phone already had an Audience.
        setAlertObj({
          "active": true,
          "type": "success",
          "msg": response.data?.audience_status === "updated"
            ? "Client created and existing audience updated successfully"
            : "client created successfully"
        });
        // Close once the banner has been seen.
        setTimeout(handlePopupClose, 1500);
      })
      .catch(error => {
        console.error('API Error:', error.response.data);
        let responseData =  error.response.data;
        if ("phone_no" in responseData){
          newFormErrors.phone_no = responseData.phone_no;
        }

        if ("client_code" in responseData){
          newFormErrors.client_code = responseData.client_code;
        }

        if ("family_code" in responseData){
          newFormErrors.family_code = responseData.family_code;
        }

        if ("client_name" in responseData) {
          newFormErrors.client_name = responseData.client_name;
        }

        if ("reporting_manager" in responseData) {
          newFormErrors.reporting_manager = responseData.reporting_manager;
        }

        if ("date_of_birth" in responseData) {
          newFormErrors.date_of_birth = responseData.date_of_birth;
        }

        setFormErrors(newFormErrors);
        // setAlertObj({
        //   "active": true,
        //   "type": "error",
        //   "msg": responseData.client_code[0]
        // });
        // Handle error, e.g., show an error message
      });
  };

  const handleEdit = () => {

    const newFormErrors = {};
    let hasErrors = false;
    let eamilPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

    const phoneError = getClientPhoneError(formData);
    if (phoneError) {
      newFormErrors.phone_no = phoneError;
      hasErrors = true;
    }

    if (formData.client_code.trim() === '') {
      newFormErrors.client_code = 'please enter a client code';
      hasErrors = true;
    }
    if (formData.client_name.trim() === '') {
      newFormErrors.client_name = 'please enter a client name';
      hasErrors = true;
    }
    console.log("formData.reporting_manager: ", formData.reporting_manager);
    if (formData.reporting_manager === null || formData.reporting_manager === '' || formData.reporting_manager === undefined) {
      newFormErrors.reporting_manager = 'please select a relationship manager';
      hasErrors = true;
    }
    
    if (hasErrors) {
      setFormErrors(newFormErrors);
      return;
    }
    // Handle editing existing client
    // Use formData to send the updated client data
    console.log('Editing client:', formData);

    // API endpoint
    const apiUrl = URL + `/clients/clients/${selectedClient.id}/`;

    const payload = {
      ...toPhonePayload(formData),
      date_of_birth: formData.date_of_birth ? dayjs(formData.date_of_birth).format('YYYY-MM-DD') : null,
    };

    // API call
    axios.put(apiUrl, payload, {
      headers: {
                "X-Tenant-Id": tenant
            }
          })
      .then(response => {
        console.log('API Response:', response.data);
        // Handle success, e.g., show a success message
        // Optionally, you can also update your UI or fetch new data
        setAlertObj({
          "active": true,
          "type": "success",
          "msg": "client updated successfully"
        });
        // Close once the banner has been seen.
        setTimeout(handlePopupClose, 1500);
      })
      .catch(error => {
        console.error('API Error:', error.response.data);
        let responseData =  error.response.data;
        console.log(responseData);

        if ("phone_no" in responseData){
          newFormErrors.phone_no = responseData.phone_no;
        }

        if ("client_code" in responseData){
          newFormErrors.client_code = responseData.client_code;
        }

        if ("family_code" in responseData){
          newFormErrors.family_code = responseData.family_code;
        }

        if ("client_name" in responseData) {
          newFormErrors.client_name = responseData.client_name;
        }

        if ("reporting_manager" in responseData) {
          newFormErrors.reporting_manager = responseData.reporting_manager;
        }

        if ("date_of_birth" in responseData) {
          newFormErrors.date_of_birth = responseData.date_of_birth;
        }

        setFormErrors(newFormErrors);
        
        // setAlertObj({
        //   "active": true,
        //   "type": "error",
        //   "msg": responseData.client_code[0]
        // });
        // Handle error, e.g., show an error message
      });
  };

  useEffect(() => {
    let timeoutId;
    
    if (alertObj.active) {
      timeoutId = setTimeout(() => {
        handleAlertClose();
      }, 5000);
    }
    
    // Cleanup function to clear the timeout when component unmounts
    // or when alertObj changes
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [alertObj]);

  return (
    <Dialog open={modalState.isOpen}>
      <DialogTitle sx={{ ...typography.h1, m: 0, p: 2 }}>Client</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handlePopupClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
          <Collapse in={alertObj.active}>
            <Alert style={formGroupStyle} onClose={handleAlertClose} severity={alertObj.type}>
              {alertObj.msg}
            </Alert>
          </Collapse>
          <FormControl fullWidth>
            <Table style={{ borderCollapse: 'collapse' }}>
              <TableBody>
                <TableRow>
                  <TableCell style={{ border: '1px solid #ddd' }}>Client Phone</TableCell>
                  <TableCell style={{ border: '1px solid #ddd' }}>
                    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                      <CountryCodeSelect
                        value={formData.country_iso}
                        onChange={(iso) => updatePhone(iso, formData.phone_no)}
                        size="small"
                        sx={{ minWidth: 200, flex: 1 }}
                      />
                      <OutlinedInput
                        name="phone_no"
                        placeholder="Please enter phone no"
                        size="small"
                        sx={{ minWidth: 180, flex: 1 }}
                        value={formData.phone_no}
                        onChange={handleChange}
                        id="client-phone"
                        error={Boolean(phoneError || formErrors.phone_no)}
                        inputProps={{ inputMode: 'numeric' }}
                      />
                    </Box>
                    <FormHelperText error>{phoneError || formErrors.phone_no}</FormHelperText>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ border: '1px solid #ddd' }}>Client Code</TableCell>
                  <TableCell style={{ border: '1px solid #ddd' }}>
                    <OutlinedInput
                      name="client_code"
                      placeholder="Please enter client code"
                      size="small"
                      value={formData.client_code}
                      onChange={handleChange}
                      id="client-code"
                    />
                    <FormHelperText error>{formErrors.client_code}</FormHelperText>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ border: '1px solid #ddd' }}>Family Code</TableCell>
                  <TableCell style={{ border: '1px solid #ddd' }}>
                    <OutlinedInput
                      name="family_code"
                      placeholder="Please enter family code"
                      size="small"
                      value={formData.family_code}
                      onChange={handleChange}
                      id="client-code"
                    />
                    <FormHelperText error>{formErrors.family_code}</FormHelperText>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ border: '1px solid #ddd' }}>Date of Birth</TableCell>
                  <TableCell style={{ border: '1px solid #ddd' }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <AppDatePicker
                        value={formData.date_of_birth}
                        onChange={handleDateChange}
                        disableFuture
                        error={!!formErrors.date_of_birth}
                        helperText={formErrors.date_of_birth}
                      />
                    </LocalizationProvider>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ border: '1px solid #ddd' }}>Client Name</TableCell>
                  <TableCell style={{ border: '1px solid #ddd' }}>
                    <OutlinedInput
                      name="client_name"
                      placeholder="Please enter client name"
                      size="small"
                      value={formData.client_name}
                      onChange={handleChange}
                      id="client-name"
                    />
                    <FormHelperText error>{formErrors.client_name}</FormHelperText>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ border: '1px solid #ddd' }}>Relationship Manager</TableCell>
                  <TableCell style={{ border: '1px solid #ddd' }}>
                    <Autocomplete
                      sx={{ width: '100%' }}
                      options={reportingManagers.map((rm) => rm.name)}
                      value={formData.reporting_manager_name || ''}
                      onChange={(event, newValue) => {
                        const selectedManager = reportingManagers.find((rm) => rm.name === newValue);
                        if (selectedManager) {
                          setFormData({ ...formData, reporting_manager: selectedManager.phone_no, reporting_manager_name: selectedManager.name });
                          clearFieldError('reporting_manager');
                        } else {
                          setFormData({ ...formData, reporting_manager: '', reporting_manager_name: '' });
                        }
                      }}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Select Relationship Manager" 
                          variant="outlined"
                        />
                      )}
                      getOptionLabel={(option) => option}
                      isOptionEqualToValue={(option, value) => option === value}
                      noOptionsText="No Relationship managers found"
                    />
                    <FormHelperText error>{formErrors.reporting_manager}</FormHelperText>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </FormControl>
      </DialogContent>
      <DialogActions>
        {selectedClient ? (
          <ButtonOne autoFocus onClick={handleEdit}>Edit</ButtonOne>
        ) : (
          <ButtonOne autoFocus onClick={handleAdd}>Add</ButtonOne>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default CustomersFormDialog;
